"use client";
import {
  Button,
  Card,
  FileInputProfile,
  Input,
  PhoneInput,
  ScreenLoader,
  SelectInput,
  SelectSearchInput,
  TextArea,
} from "@/components";
import { useFormStepOneCompany } from "@/hooks";
import { OptionsCompany } from "@/types";
import { FormProvider } from "react-hook-form";
import { FaLinkedinIn } from "react-icons/fa6";
import { useAuth } from "@/providers/supabase-auth-provider";
import React from "react";

type FormStepOneProp = {
  options: {
    options?: OptionsCompany;
    optionError: boolean;
    optionIsLoading: boolean;
  };
  setStep: (step: number) => void;
};

export default function FormStepOneCompany({
  options,
  setStep,
}: FormStepOneProp) {
  const {
    generalInformation,
    methods,
    countries,
    countryList,
    isLoadingCountry,
    setCountrySearchQuery,
    hasMoreCountries,
    fetchNextCountriesPage,
    setPhoneCountrySearchQuery,
    hasMorePhoneCountries,
    fetchNextPhoneCountriesPage,
    isFetchingNextPhoneCountriesPage,
    statesList,
    isLoadingState,
    onSubmit,
  } = useFormStepOneCompany(setStep);

  const { user } = useAuth();

  React.useEffect(() => {
    const metaPhone = (user?.user_metadata as any)?.phone;
    if (!metaPhone || typeof metaPhone !== "object") return;
    const current = methods.getValues("phone_number");
    const alreadyFilled = Boolean(current?.phone_number) || Boolean(current?.country_id);
    if (alreadyFilled) return;
    methods.setValue(
      "phone_number",
      {
        country_id: typeof metaPhone.country_id === "string" ? metaPhone.country_id : "",
        country_phone_code:
          typeof metaPhone.country_phone_code === "string" ? metaPhone.country_phone_code : "",
        phone_number: typeof metaPhone.phone_number === "string" ? metaPhone.phone_number : "",
      } as any,
      { shouldDirty: false, shouldValidate: false }
    );
  }, [user, methods]);

  React.useEffect(() => {
    // If store has generalInformation, merge metadata phone once into initial reset to avoid overwriting user edits
    const gi: any = (generalInformation as any) || {};
    if (!gi || Object.keys(gi).length === 0) return;
    const metaPhone = (user?.user_metadata as any)?.phone;
    const hasMetaPhone = metaPhone && typeof metaPhone === "object";
    const mergedPhone = (() => {
      const fromApi = gi.phone_number || {};
      const phone_number = fromApi.phone_number || (hasMetaPhone ? metaPhone.phone_number : "");
      const country_id = fromApi.country_id || (hasMetaPhone ? metaPhone.country_id : "");
      const country_phone_code = fromApi.country_phone_code || (hasMetaPhone ? metaPhone.country_phone_code : "");
      return { country_id, country_phone_code, phone_number };
    })();
    methods.reset({
      ...gi,
      phone_number: mergedPhone,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!generalInformation, user]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        {methods.formState.isSubmitting && <ScreenLoader />}
        <Card>
          <div className="flex flex-col-reverse sm:flex-row">
            {/* Col 1  */}
            <div className="flex-1 flex-col">
              <div className="flex justify-center items-center">
                <FileInputProfile
                  name="picture_profile"
                  fallbackSrc={
                    generalInformation?.picture_profile as string | undefined
                  }
                />
              </div>
              <TextArea
                name="cover_letter"
                label="Cover Letter"
                rows={10}
                maxLength={1000}
                placeholder="Add a cover letter about you"
              />
            </div>
            <div className="flex-2 px-4">
              <h2 className="font-semibold text-2xl">
                {methods.watch("first_name") || "Your Name"}{" "}
                {methods.watch("last_names")}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  name="first_name"
                  label="First Name"
                  className="w-full"
                  placeholder="Enter your first name"
                  required
                />
                <Input
                  name="last_names"
                  label="Last Name"
                  className="w-full"
                  placeholder="Enter your last name"
                  required
                />
                <SelectSearchInput
                  name="location_country"
                  label="Location Country"
                  placeholder="Enter the County where you live"
                  className={`${countryList.length === 0
                    ? "opacity-50 pointer-events-none select-none"
                    : ""
                    }`}
                  required
                  items={countryList}
                  loading={isLoadingCountry}
                  onSearch={setCountrySearchQuery}
                  hasMore={hasMoreCountries}
                  onLoadMore={() => hasMoreCountries && fetchNextCountriesPage()}
                  onResetPage={() => setCountrySearchQuery("")}
                  classNameContainer="w-full z-100"
                />
                <SelectSearchInput
                  name="location_state"
                  placeholder="Enter the State where you live"
                  label="Location State"
                  items={statesList}
                  required
                  loading={isLoadingState}
                  classNameContainer="w-full z-100"
                />
                <PhoneInput
                  name="phone_number"
                  countries={countries}
                  defaultCountryId={methods.watch("location_country")?.id || ""}
                  label="Phone Number"
                  required
                  isLoading={isLoadingCountry}
                  onSearchChange={setPhoneCountrySearchQuery}
                  hasNextPage={hasMorePhoneCountries}
                  fetchNextPage={fetchNextPhoneCountriesPage}
                  isFetchingNextPage={isFetchingNextPhoneCountriesPage}
                />
                <SelectInput
                  name="job_area"
                  options={options.options?.job_area || []}
                  label="Your Job Area"
                  placeholder="Enter your job area"
                />
                <SelectInput
                  name="responsibility_level"
                  options={options.options?.responsibility_level || []}
                  label="Responsibility level"
                  placeholder="Enter your responsibility level"
                />
                <Input
                  name="job_title"
                  label="Your Job Title"
                  className="w-full"
                  placeholder="Enter your job title"
                />
              </div>

              <div className="border-t-2 border-secondary-100 my-4"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-1.5">
                  <div className="flex justify-center items-center rounded-full bg-blue-500 text-white w-7.5 h-7.5 text-lg mt-4">
                    <FaLinkedinIn />
                  </div>
                  <Input
                    classNameContainer="flex-1"
                    placeholder="Your LinkedIn Link"
                    name="linkedin_url"
                  />
                </div>
              </div>
              <div className="border-t-2 border-secondary-100 my-4"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SelectInput
                  name="find_jobbi"
                  options={options.options?.find_jobbi || []}
                  label="How did you hear about Jobbi?"
                  placeholder="Select..."
                />
              </div>
            </div>
          </div>
        </Card>
        <div className="my-3  flex justify-between md:justify-end gap-4">
          <Button
            type="submit"
            rounded="rounded-xl"
            className="w-52"
            isLoading={methods.formState.isSubmitting}
          >
            Next
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
